import {abortUpload, completeUpload, initiateUpload, signParts} from './MediaAssetService';
import {getClientConfig} from './ClientConfigService';
import {MAX_DIRECT_VIDEO_UPLOAD_MB, MAX_VIDEO_UPLOAD_MB, getVideoTooLargeMessage} from '../utils/videoUtils';
import type {InitiateUploadResponse, UploadPartUrl, UploadedPart} from '../api/generated';

/**
 * MediaUploadService - file uploads with progress tracking.
 *
 * Two transports live behind one signature:
 *
 * - **VIDEO** goes direct to S3 as a resumable multipart upload, where the environment has that enabled.
 *   Otherwise it falls back to the API path below. The API only brokers signatures, so a
 *   1 GB exercise demo never enters the server's heap or its 150 MB multipart limit, individual parts
 *   retry instead of restarting the whole file, and progress reflects real bytes on the wire.
 * - **IMAGE / DOC** stay on `POST /admin/media/upload`. They are small, and that endpoint does the
 *   server-side image processing the direct path deliberately has no equivalent for. The server also
 *   rejects anything but VIDEO on the direct path.
 *
 * Callers see the same `(file, onUploadProgress, onProcessingProgress) => Promise<cdnUrl>` either way.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

export type FileTypeName = 'IMAGE' | 'VIDEO' | 'DOC';
export type ProgressCallback = (percent: number) => void;

/**
 * Parts uploaded at once. Enough to saturate a typical uplink without opening so many sockets that
 * per-part progress becomes noise or the browser starts queueing them anyway.
 */
const PART_CONCURRENCY = 4;

/** Attempts per part. A part is a bounded retry unit — this is the whole point of multipart here. */
const PART_MAX_ATTEMPTS = 3;

/** Mirrors the server's progress.media.upload.allowed-video-content-types. */
const ALLOWED_VIDEO_CONTENT_TYPES = [
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
    'video/webm',
    'video/mpeg',
];

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
    mp4: 'video/mp4',
    m4v: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    webm: 'video/webm',
    mpeg: 'video/mpeg',
    mpg: 'video/mpeg',
};

// ---------------------------------------------------------------------------------------------
// Direct-to-S3 video upload
// ---------------------------------------------------------------------------------------------

/**
 * Browsers leave `file.type` empty often enough to matter (notably for `.mov` picked from some file
 * managers), and the server rejects an unrecognised content type outright, so fall back to the
 * extension before giving up.
 */
export const resolveVideoContentType = (file: File): string => {
    const declared = (file.type || '').toLowerCase();
    if (ALLOWED_VIDEO_CONTENT_TYPES.includes(declared)) {
        return declared;
    }
    const extension = file.name?.includes('.')
        ? file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase()
        : '';
    const inferred = CONTENT_TYPE_BY_EXTENSION[extension];
    if (inferred) {
        return inferred;
    }
    throw new Error('פורמט הווידאו אינו נתמך. יש להעלות MP4, WEBM, MOV או AVI');
};

/**
 * PUT one part straight to S3.
 *
 * Deliberately a bare XHR: these requests carry a presigned signature and must NOT get the app's
 * Authorization header, which the generated client's interceptor adds to everything it sends. The blob
 * is sent without an explicit Content-Type because the server does not sign one — `File.slice()` yields
 * a typeless blob, so the browser adds no header and the signature matches.
 */
const putPart = (url: string, blob: Blob, onBytes: (loaded: number) => void): Promise<string> =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
                onBytes(e.loaded);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status < 200 || xhr.status >= 300) {
                reject(new Error(`S3 rejected part upload with status ${xhr.status}`));
                return;
            }
            const eTag = xhr.getResponseHeader('ETag');
            if (!eTag) {
                // The part stored fine; we just can't read its ETag, and completion needs every one of
                // them. In practice this means the bucket's CORS rule is missing ExposeHeaders: ETag.
                reject(new Error('S3 did not expose an ETag for the uploaded part (check bucket CORS)'));
                return;
            }
            onBytes(blob.size);
            resolve(eTag);
        });

        xhr.addEventListener('error', () => reject(new Error('Network error while uploading a part')));
        xhr.addEventListener('timeout', () => reject(new Error('Timed out while uploading a part')));

        xhr.open('PUT', url);
        xhr.send(blob);
    });

/**
 * Upload one part, retrying with a freshly signed URL.
 *
 * Re-signing on every retry rather than only on 403 is intentional: a 1 GB upload on a slow uplink can
 * outlive any presign TTL short enough to be safe to hand out, and by the time a part fails we can't
 * always tell an expired grant from a dropped connection. Signing again is cheap and fixes both.
 */
const uploadPartWithRetry = async (
    assetId: number,
    partNumber: number,
    blob: Blob,
    urlByPart: Map<number, string>,
    onBytes: (loaded: number) => void,
): Promise<string> => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= PART_MAX_ATTEMPTS; attempt++) {
        const url = urlByPart.get(partNumber);
        if (!url) {
            throw new Error(`No upload URL for part ${partNumber}`);
        }
        try {
            return await putPart(url, blob, onBytes);
        } catch (error) {
            lastError = error;
            // The retry re-sends from byte zero, so drop this part's contribution or the aggregate
            // percentage would keep the abandoned attempt's bytes and could exceed 100.
            onBytes(0);

            if (attempt === PART_MAX_ATTEMPTS) break;

            try {
                const [fresh] = await signParts(assetId, [partNumber]);
                if (fresh?.url) urlByPart.set(partNumber, fresh.url);
            } catch {
                // Keep the original transfer error — a failure to re-sign is a symptom, not the cause.
            }
        }
    }

    throw lastError instanceof Error ? lastError : new Error('Failed to upload part');
};

/** Slice the file across the planned parts and upload them with bounded concurrency. */
const uploadAllParts = async (
    file: File,
    plan: InitiateUploadResponse,
    onUploadProgress?: ProgressCallback,
): Promise<UploadedPart[]> => {
    const assetId = plan.assetId as number;
    const partSize = plan.partSizeBytes as number;
    const partCount = plan.partCount as number;

    const urlByPart = new Map<number, string>(
        (plan.parts ?? []).map((p: UploadPartUrl) => [p.partNumber as number, p.url as string]),
    );

    // Indexed by part number, so index 0 is unused and the sum is over live parts only.
    const loadedByPart = new Array<number>(partCount + 1).fill(0);
    const eTagByPart = new Array<string>(partCount + 1);

    let lastReported = -1;
    const reportProgress = () => {
        const loaded = loadedByPart.reduce((sum, n) => sum + n, 0);
        const percent = Math.min(100, Math.round((loaded / file.size) * 100));
        if (percent !== lastReported) {
            lastReported = percent;
            onUploadProgress?.(percent);
        }
    };

    let nextPart = 1;
    const worker = async () => {
        for (let partNumber = nextPart++; partNumber <= partCount; partNumber = nextPart++) {
            const start = (partNumber - 1) * partSize;
            const blob = file.slice(start, Math.min(start + partSize, file.size));
            eTagByPart[partNumber] = await uploadPartWithRetry(
                assetId,
                partNumber,
                blob,
                urlByPart,
                (loaded) => {
                    loadedByPart[partNumber] = loaded;
                    reportProgress();
                },
            );
        }
    };

    await Promise.all(
        Array.from({length: Math.min(PART_CONCURRENCY, partCount)}, () => worker()),
    );

    return Array.from({length: partCount}, (_, i) => ({
        partNumber: i + 1,
        eTag: eTagByPart[i + 1],
    }));
};

/**
 * Resumable direct-to-S3 upload: initiate, PUT every part, complete.
 *
 * Any failure aborts the upload server-side. That matters beyond tidiness — the abandoned S3 parts are
 * billed until collected, and each unfinished upload holds one of the caller's few concurrent-upload
 * slots, so skipping it would let a couple of failures lock the user out of uploading at all.
 */
export const uploadVideoDirect = async (
    file: File,
    onUploadProgress?: ProgressCallback,
    onProcessingProgress?: ProgressCallback,
): Promise<string> => {
    if (file.size > MAX_DIRECT_VIDEO_UPLOAD_MB * 1024 * 1024) {
        throw new Error(getVideoTooLargeMessage(MAX_DIRECT_VIDEO_UPLOAD_MB));
    }

    const contentType = resolveVideoContentType(file);
    const plan = await initiateUpload({
        fileType: 'VIDEO',
        contentType,
        sizeBytes: file.size,
        fileName: file.name,
    });

    const assetId = plan.assetId;
    if (assetId == null) {
        throw new Error('Media upload response was missing an asset id');
    }

    try {
        onUploadProgress?.(0);
        const parts = await uploadAllParts(file, plan, onUploadProgress);

        // Server-side finalisation (assembling the parts, promoting the object to the CDN bucket). There
        // is no byte-level signal to report here, so this reports start and finish rather than inventing
        // intermediate values.
        onProcessingProgress?.(0);
        const asset = await completeUpload(assetId, parts);
        onProcessingProgress?.(100);

        if (!asset.playbackUrl) {
            throw new Error('Upload completed but no playback URL was returned');
        }
        return asset.playbackUrl;
    } catch (error) {
        await abortUpload(assetId).catch(() => {
            // Best effort. The bucket's abort-incomplete-multipart lifecycle rule is the backstop, and
            // surfacing a cleanup failure here would mask the error the user actually needs to see.
        });
        throw error;
    }
};

// ---------------------------------------------------------------------------------------------
// Legacy multipart upload (images and documents)
// ---------------------------------------------------------------------------------------------

/**
 * Streams the whole file through the API, which replies with an SSE stream carrying upload/compression
 * progress and finally the CDN URL.
 *
 * TODO(openapi-migration): SSE-over-multipart, which the generated client cannot model.
 */
const uploadThroughApi = (
    file: File,
    fileType: FileTypeName,
    onUploadProgress?: ProgressCallback,
    onCompressionProgress?: ProgressCallback,
): Promise<string> =>
    new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', fileType);

        const xhr = new XMLHttpRequest();
        let lastParsedIndex = 0;

        // Track upload progress (Phase 1)
        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onUploadProgress) {
                const percent = Math.round((e.loaded / e.total) * 100);
                onUploadProgress(percent);
            }
        });

        // Parse SSE response for compression progress (Phase 2)
        xhr.addEventListener('readystatechange', () => {
            if (xhr.readyState === 3 || xhr.readyState === 4) {
                // Parse only new data since last check
                const newData = xhr.responseText.substring(lastParsedIndex);
                lastParsedIndex = xhr.responseText.length;

                const lines = newData.split('\n');
                for (const line of lines) {
                    if (line.startsWith('data:')) {
                        try {
                            const event = JSON.parse(line.slice(5));

                            if (event.phase === 'compression' && onCompressionProgress) {
                                onCompressionProgress(event.percent);
                            } else if (event.phase === 'complete' && event.cdnUrl) {
                                resolve(event.cdnUrl);
                            } else if (event.phase === 'error') {
                                reject(new Error(event.errorMessage || 'Upload failed'));
                            }
                        } catch (e) {
                            // Ignore parse errors for incomplete lines
                        }
                    }
                }
            }
        });

        xhr.addEventListener('load', () => {
            // Final check for completion if not already resolved
            if (xhr.status >= 200 && xhr.status < 300) {
                const lines = xhr.responseText.split('\n');
                for (const line of lines) {
                    if (line.startsWith('data:')) {
                        try {
                            const event = JSON.parse(line.slice(5));
                            if (event.phase === 'complete' && event.cdnUrl) {
                                resolve(event.cdnUrl);
                                return;
                            }
                        } catch (e) {}
                    }
                }
            }
            // If we got here without resolving, something went wrong
            if (xhr.status >= 400) {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        });

        xhr.addEventListener('error', () => {
            reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('timeout', () => {
            reject(new Error('Upload timed out'));
        });

        // Set timeout for large files (10 minutes)
        xhr.timeout = 10 * 60 * 1000;

        const token = localStorage.getItem('auth');
        xhr.open('POST', `${API_BASE}/admin/media/upload`);

        if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.send(formData);
    });

// ---------------------------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------------------------

/**
 * Upload a media file with progress tracking.
 *
 * <p>Which transport a VIDEO takes is the environment's decision, read from `GET /admin/config` — see
 * ClientConfigService. With the direct pipeline switched off, video posts through the API exactly as it
 * did before that pipeline existed. Asking here rather than at every call site keeps the two pages that
 * upload video from having to know the flag exists, and guarantees they cannot disagree about it.
 *
 * @param file The file to upload
 * @param fileType Type of file — VIDEO may take the direct-to-S3 path
 * @param onUploadProgress Callback for upload progress (0-100)
 * @param onCompressionProgress Callback for server-side processing progress (0-100)
 * @returns CDN URL of the uploaded file
 */
export const uploadMedia = async (
    file: File,
    fileType: FileTypeName,
    onUploadProgress?: ProgressCallback,
    onCompressionProgress?: ProgressCallback,
): Promise<string> => {
    if (fileType !== 'VIDEO') {
        return uploadThroughApi(file, fileType, onUploadProgress, onCompressionProgress);
    }

    const {directVideoUploadEnabled} = await getClientConfig();
    if (directVideoUploadEnabled) {
        return uploadVideoDirect(file, onUploadProgress, onCompressionProgress);
    }

    // The legacy path streams the whole file through the API, so it is bound by the servlet's multipart
    // limit rather than the far larger direct-upload cap. Checked here because the two ceilings differ by
    // most of a gigabyte: without it a file the UI accepted would be rejected by the container with no
    // usable message.
    if (file.size > MAX_VIDEO_UPLOAD_MB * 1024 * 1024) {
        throw new Error(getVideoTooLargeMessage(MAX_VIDEO_UPLOAD_MB));
    }
    return uploadThroughApi(file, fileType, onUploadProgress, onCompressionProgress);
};

/** Upload an image file. Convenience wrapper for uploadMedia with IMAGE type. */
export const uploadImage = (
    file: File,
    onUploadProgress?: ProgressCallback,
    onCompressionProgress?: ProgressCallback,
): Promise<string> => uploadMedia(file, 'IMAGE', onUploadProgress, onCompressionProgress);

/** Upload a video file. Convenience wrapper for uploadMedia with VIDEO type. */
export const uploadVideo = (
    file: File,
    onUploadProgress?: ProgressCallback,
    onCompressionProgress?: ProgressCallback,
): Promise<string> => uploadMedia(file, 'VIDEO', onUploadProgress, onCompressionProgress);

/** Upload a document file. Convenience wrapper for uploadMedia with DOC type. */
export const uploadDocument = (
    file: File,
    onUploadProgress?: ProgressCallback,
    onCompressionProgress?: ProgressCallback,
): Promise<string> => uploadMedia(file, 'DOC', onUploadProgress, onCompressionProgress);

const mediaUploadService = {
    uploadMedia,
    uploadImage,
    uploadVideo,
    uploadDocument,
};

export default mediaUploadService;
